/** TanStack Query hooks for analytics (time estimates, learning metrics). */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { CourseAnalyticsResponse, LessonAnalyticsResponse } from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const analyticsKeys = {
  all: ['f', 'analytics'] as const,
  courseEstimates: (courseId: string) =>
    [...analyticsKeys.all, 'course-estimates', courseId] as const,
  lessonEstimate: (lessonId: string) =>
    [...analyticsKeys.all, 'lesson-estimate', lessonId] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch time estimates for a course and its content hierarchy. */
export function useCourseTimeEstimates(courseId: string | undefined) {
  return useQuery<CourseAnalyticsResponse>({
    queryKey: analyticsKeys.courseEstimates(courseId!),
    queryFn: () => api.get<CourseAnalyticsResponse>(`/me/analytics/courses/${courseId}`),
    staleTime: 60 * 60_000, // 1 hour — analytics data changes infrequently
    enabled: !!courseId,
  });
}

/** Fetch time estimate breakdown for a single lesson. */
export function useLessonTimeEstimate(lessonId: string | undefined) {
  return useQuery<LessonAnalyticsResponse>({
    queryKey: analyticsKeys.lessonEstimate(lessonId!),
    queryFn: () => api.get<LessonAnalyticsResponse>(`/me/analytics/lessons/${lessonId}`),
    staleTime: 60 * 60_000,
    enabled: !!lessonId,
  });
}
